import React, { useState } from 'react';
import { InfoCard } from '@backstage/core-components';
import {
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  Box,
} from '@material-ui/core';
import { useEntity } from '@backstage/plugin-catalog-react';
import { EditNamespacesDialog } from '../../pages/ProductPage/EditNamespacesDialog';
import { useCurrentProfile } from '../../../hooks';
import CloudIcon from '@material-ui/icons/Cloud';
import { RequestUpdateButton } from '../../styles/Buttons';

const groupClusters = (clusters: any[]) => {
  const grouped: { [key: string]: any[] } = {};
  clusters.forEach(cluster => {
    const group = cluster.group;
    if (!grouped[group]) grouped[group] = [];
    grouped[group].push(cluster);
  });
  return Object.keys(grouped).map(group => ({
    group,
    clusters: grouped[group],
  }));
};

export const UpdateNamespacesCard = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const entity = useEntity().entity;
  const { identity } = useCurrentProfile();
  let clustersFromEntity = (entity?.spec as any).data.clusters as any[];
  if (clustersFromEntity) {
    clustersFromEntity = clustersFromEntity.map(cluster => ({
      ...cluster,
      clusterGroup: cluster.group,
    }));
  }
  const displayClusterGroups = groupClusters(clustersFromEntity);
  const productName: string = String(
    entity?.metadata?.annotations?.['schema.dp.netrtl.com/legacy-short-key'],
  );
  // The model api addresses the team by its CMDB uid, spec.owner holds the
  // entity name of the team, which is its vuid.
  const cmdbTeamUID: string = String(
    entity?.spec?.ownerUid ?? entity?.spec?.owner,
  );
  const cmdbGroupName: string = String(entity?.spec?.ownerName);
  const hasClusters = clustersFromEntity && clustersFromEntity.length > 0;
  const actionButton = hasClusters ? (
    <RequestUpdateButton onClick={() => setDialogOpen(true)} />
  ) : undefined;

  return (
    <>
      <InfoCard
        title={<span>Kubernetes Namespaces</span>}
        variant="gridItem"
        action={actionButton}
      >
        {displayClusterGroups.length > 0 ? (
          <>
            {displayClusterGroups.map(({ group, clusters }) => {
              const provider = clusters[0]?.provider;
              return (
                <Box key={group} mb={2} pb={1}>
                  <Box
                    display="flex"
                    alignItems="center"
                    mb={1}
                    style={{ gap: 8 }}
                  >
                    <Chip
                      icon={<CloudIcon />}
                      label={provider}
                      color="default"
                      size="small"
                    />
                    <Typography variant="h6">{group} cluster group</Typography>
                  </Box>
                  <List>
                    {clusters.map((cluster: any) => (
                      <ListItem key={cluster.name} alignItems="flex-start">
                        <ListItemText
                          primary={
                            <Box
                              display="flex"
                              alignItems="center"
                              style={{ gap: 8 }}
                            >
                              <Typography variant="subtitle1">
                                {cluster.name}
                              </Typography>
                              <Box display="flex" alignItems="flex-end">
                                <Chip
                                  label={cluster.environment}
                                  color="primary"
                                  size="small"
                                />
                              </Box>
                            </Box>
                          }
                          secondary={
                            <Box
                              display="flex"
                              flexWrap="wrap"
                              mt={1}
                              style={{ gap: 8 }}
                            >
                              {cluster.namespaces.map((ns: string) => (
                                <Box
                                  key={ns}
                                  display="flex"
                                  alignItems="center"
                                >
                                  <Chip
                                    label={ns}
                                    color="default"
                                    size="small"
                                  />
                                </Box>
                              ))}
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              );
            })}
          </>
        ) : (
          <Typography>No clusters found for this group.</Typography>
        )}
      </InfoCard>

      {identity && (
        <EditNamespacesDialog
          openDialog={dialogOpen}
          toggleDialog={() => setDialogOpen(false)}
          namespaceChanges={clustersFromEntity}
          productName={productName}
          cmdbTeamUID={cmdbTeamUID}
          cmdbGroupName={cmdbGroupName}
          currentUserMail={identity!.email!.toLowerCase()}
        />
      )}
    </>
  );
};
